import React from "react"
import { Box, CircularProgress, Divider, Typography, useTheme } from "@mui/material"
import { RoundedBox } from "@/components/conatiners/rounded-box"
import SecondaryButton from "@/components/common/SecondaryButton"
import {
  getOrderSectionCardSx,
  ORDER_CARD_CONTENT_PX,
  ORDER_CARD_CONTENT_PY,
  ORDER_CARD_HEADER_P,
} from "@/utils/order-status"
import { formatOrderDetailsDateTime } from "@/utils/format-order-details-datetime"
import { useOrderRefundDetails } from "@/hooks/use-order-refund-details"
import {
  RefundDetailsStatus,
  RefundDetailsStatusLabel,
  type OrderRefundDetailsUI,
  type RefundHistoryEntryUI,
} from "@/domain/order/order-refund-details"

export type OrderRefundDetailsProps = {
  orderId: string
}

/** Formats in the refund's own currency (from the payment/refund records), never the storefront default. */
function formatRefundMoney(amount: number, currencyCode: string | null): string {
  const code = currencyCode?.trim()
  if (code) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)
    } catch {
      return `${code} ${amount.toFixed(2)}`
    }
  }
  return amount.toFixed(2)
}

function MetaRow({ label, value }: { label: string; value: string }) {
  const theme = useTheme()
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(0.5) }}>
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: theme.typography.fontWeightBold }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.primary, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  )
}

function HistoryRow({ entry, currencyCode }: { entry: RefundHistoryEntryUI; currencyCode: string | null }) {
  const theme = useTheme()
  const completed = entry.status === RefundDetailsStatus.Refunded
  const when = formatOrderDetailsDateTime(completed ? entry.completedAtUtc : entry.requestedAtUtc)
  const whenLabel = when ? `${completed ? "Completed" : "Requested"} ${when}` : ""

  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        py: theme.spacing(0.75),
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(0.25), minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: theme.typography.fontWeightBold }}>
          {RefundDetailsStatusLabel[entry.status]}
        </Typography>
        {whenLabel ? (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {whenLabel}
          </Typography>
        ) : null}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: theme.typography.fontWeightBold, whiteSpace: "nowrap" }}>
        {formatRefundMoney(entry.amount, currencyCode)}
      </Typography>
    </Box>
  )
}

function RefundDetailsBody({ details }: { details: OrderRefundDetailsUI }) {
  const theme = useTheme()

  if (details.kind === "no-record") {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
        {details.message}
      </Typography>
    )
  }

  const money = (value: number | null) => (value === null ? null : formatRefundMoney(value, details.currencyCode))
  const original = money(details.originalPaymentAmount)
  const refunded = money(details.refundedAmount)
  const inProgress =
    details.inProgressAmount !== null && details.inProgressAmount > 0 ? money(details.inProgressAmount) : null

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1.25) }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(0.5) }}>
        <Typography variant="subtitle2" sx={{ fontWeight: theme.typography.fontWeightBold }}>
          {RefundDetailsStatusLabel[details.status]}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
          {details.message}
        </Typography>
      </Box>

      {original || refunded || inProgress ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
            gap: theme.spacing(1),
          }}
        >
          {original ? <MetaRow label="Original payment" value={original} /> : null}
          {refunded ? <MetaRow label="Total refunded" value={refunded} /> : null}
          {inProgress ? <MetaRow label="Refund in progress" value={inProgress} /> : null}
        </Box>
      ) : null}

      {details.history.length > 0 ? (
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: theme.typography.fontWeightBold }}>
            Refund history
          </Typography>
          <Box component="ul" aria-label="Refund history" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {details.history.map((entry, index) => (
              // Entries deliberately carry no identifiers; the list is static once loaded.
              // eslint-disable-next-line react/no-array-index-key
              <HistoryRow key={index} entry={entry} currencyCode={details.currencyCode} />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

/**
 * Refund section for a cancelled, paid order. Nothing is requested until the customer taps
 * "View refund details"; the server response is shown as-is and never inferred from order or
 * payment status.
 */
export function OrderRefundDetails({ orderId }: OrderRefundDetailsProps) {
  const theme = useTheme()
  const regionId = React.useId()
  const { state, load } = useOrderRefundDetails(orderId)

  return (
    <RoundedBox aria-label="Refund" sx={{ ...getOrderSectionCardSx(theme), mb: theme.spacing(1) }}>
      <Box sx={{ p: ORDER_CARD_HEADER_P }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: theme.typography.fontWeightBold }}>
          Refund
        </Typography>
      </Box>
      <Divider />
      <Box
        id={regionId}
        aria-live="polite"
        sx={{ py: ORDER_CARD_CONTENT_PY, px: ORDER_CARD_CONTENT_PX, display: "flex", flexDirection: "column", gap: theme.spacing(1.25) }}
      >
        {state.phase === "idle" ? (
          <SecondaryButton fullWidth onClick={() => void load()} aria-controls={regionId}>
            View refund details →
          </SecondaryButton>
        ) : null}

        {state.phase === "loading" ? (
          <Box role="status" sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
            <CircularProgress size={18} aria-hidden />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Loading refund details…
            </Typography>
          </Box>
        ) : null}

        {state.phase === "error" ? (
          <>
            <Typography variant="body2" role="alert" sx={{ color: theme.palette.error.main }}>
              {state.message}
            </Typography>
            <SecondaryButton fullWidth onClick={() => void load()}>
              Try again
            </SecondaryButton>
          </>
        ) : null}

        {state.phase === "loaded" ? <RefundDetailsBody details={state.details} /> : null}
      </Box>
    </RoundedBox>
  )
}
